/**
 * 企业微信API服务
 */
import request from '../utils/request';
import { logInfo } from '../utils/logger';

export interface WeChatAuthResponse {
  success: boolean;
  access_token?: string;
  refresh_token?: string;
  token_type?: string;
  user_info?: {
    id: number;
    username: string;
    real_name: string;
    email: string;
    role: string;
    wechat_userid: string;
    wechat_avatar: string;
  };
  message: string;
}

export interface WeChatOAuthUrlResponse {
  authorize_url: string;
}

export interface WeChatConfigResponse {
  configured: boolean;
  message?: string;
  corp_id?: string;
  agent_id?: string;
  has_access_token?: boolean;
  token_expires_at?: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * 获取企业微信OAuth授权URL
 */
export async function getWeChatOAuthUrl(
  redirectUri: string, 
  state: string = 'STATE',
  scope: string = 'snsapi_base'
): Promise<WeChatOAuthUrlResponse> {
  return request.get('/wechat/oauth/authorize', {
    params: {
      redirect_uri: redirectUri,
      state: state,
      scope: scope
    }
  });
}

/**
 * 企业微信OAuth回调处理
 */
export async function weChatOAuthCallback(code: string, state: string = 'STATE'): Promise<WeChatAuthResponse> {
  return request.post('/wechat/oauth/callback', {
    code,
    state
  });
}

/**
 * 获取企业微信配置状态（公开接口，用于登录页面）
 */
export async function getWeChatConfig(): Promise<WeChatConfigResponse> {
  return request.get('/wechat/config/status');
}

/**
 * 获取企业微信完整配置（需要管理员权限）
 */
export async function getWeChatFullConfig(): Promise<WeChatConfigResponse> {
  return request.get('/wechat/config');
}

/**
 * 测试企业微信连接
 */
export async function testWeChatConnection(): Promise<{ success: boolean; message: string }> {
  return request.post('/wechat/test/connection');
}

/**
 * 获取当前用户的企业微信信息
 */
export async function getCurrentUserWeChatInfo(): Promise<{
  userid: string | null;
  avatar: string | null;
  last_sync_at: string | null;
  has_wechat_binding: boolean;
}> {
  return request.get('/wechat/user/info');
}

/**
 * 企业微信登录
 * @param redirectUri 登录成功后的重定向地址
 */
export async function weChatLogin(redirectUri?: string): Promise<void> {
  try {
    // 检测是否在企业微信内
    const ua = navigator.userAgent.toLowerCase();
    // 企业微信的 UA 包含 wxwork 或 micromessenger
    const isInWeChatWork = ua.includes('wxwork') || (ua.includes('micromessenger') && ua.includes('wxwork'));
    
    logInfo('=== 企业微信登录调试信息 ===');
    logInfo('用户代理:', ua);
    logInfo('是否在企业微信内:', isInWeChatWork);
    logInfo('当前 URL:', window.location.href);

    if (isInWeChatWork) {
      // 在企业微信内，使用静默授权
      logInfo('检测到企业微信环境，使用静默授权');
      
      // 构造静默授权 URL
      const protocol = window.location.protocol;
      const host = window.location.host;
      const currentUrl = `${protocol}//${host}/login`;
      
      // 检查 URL 中是否已有 code
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      
      if (code) {
        // 已有 code，直接处理回调
        logInfo('URL 中已有 code，直接处理登录:', code);
        const response = await weChatOAuthCallback(code);
        
        if (response.success && response.access_token) {
          // 登录成功，保存 token 并跳转
          localStorage.setItem('token', response.access_token);
          window.location.href = '/';
        } else {
          throw new Error(response.message || '登录失败');
        }
      } else {
        // 没有 code，需要获取授权（使用静默授权）
        logInfo('URL 中没有 code，跳转获取授权（静默）');
        const response = await getWeChatOAuthUrl(currentUrl, 'STATE', 'snsapi_base');
        
        if (response.authorize_url) {
          window.location.href = response.authorize_url;
        } else {
          throw new Error('无法获取企业微信授权URL');
        }
      }
    } else {
      // 不在企业微信内，使用扫码登录
      console.log('不在企业微信环境，使用扫码登录');
      
      const protocol = window.location.protocol;
      const host = window.location.host;
      const defaultRedirectUri = `${protocol}//${host}/login/wechat/callback`;
      const finalRedirectUri = redirectUri || defaultRedirectUri;

      console.log('企业微信登录开始，重定向地址:', finalRedirectUri);

      // 获取授权URL（扫码登录使用 qrConnect）
      const response = await getWeChatOAuthUrl(finalRedirectUri, 'STATE', 'qrConnect');

      console.log('获取到的授权URL响应:', response);

      if (response.authorize_url) {
        console.log('准备跳转到授权页面:', response.authorize_url);
        // 跳转到企业微信授权页面
        window.location.href = response.authorize_url;
      } else {
        console.error('授权URL响应中没有authorize_url字段:', response);
        throw new Error('无法获取企业微信授权URL');
      }
    }
  } catch (error) {
    console.error('企业微信登录失败，详细错误:', error);
    console.error('错误类型:', typeof error);
    console.error('错误信息:', error instanceof Error ? error.message : String(error));
    throw error;
  }
}


/**
 * 企业微信通讯录同步相关接口
 */

export interface SyncResult {
  success: boolean;
  sync_id?: number;
  sync_time: string;
  user_stats: {
    total: number;
    created: number;
    updated: number;
    errors: number;
  };
  departments_synced: boolean;
  duration_seconds?: number;
  message: string;
}

export interface SyncStatus {
  has_sync: boolean;
  sync_id?: number;
  sync_type?: string;
  sync_status?: string;
  total_count?: number;
  success_count?: number;
  error_count?: number;
  started_at?: string;
  completed_at?: string;
  message?: string;
}

export interface SyncLog {
  id: number;
  sync_type: string;
  sync_status: string;
  total_count: number;
  created_count: number;
  updated_count: number;
  error_count: number;
  error_msg?: string;
  started_at: string;
  completed_at?: string;
  duration_seconds: number;
}

export interface SyncLogsResponse {
  items: SyncLog[];
  total: number;
  skip: number;
  limit: number;
}

/**
 * 手动触发企业微信通讯录同步
 */
export async function syncWeChatUsers(): Promise<SyncResult> {
  return request.post('/wechat/sync/users');
}

/**
 * 获取最近一次同步状态
 */
export async function getSyncStatus(): Promise<SyncStatus> {
  return request.get('/wechat/sync/status');
}

/**
 * 获取同步历史记录
 */
export async function getSyncLogs(skip: number = 0, limit: number = 20): Promise<SyncLogsResponse> {
  return request.get('/wechat/sync/logs', {
    params: { skip, limit }
  });
}
